// High-Lift composite registration client (FXC-9009 / blown-lift × Tailor).
//
// Registers a plain-flap high-lift design's multifidelity database with thread-api:
// a flexfoil ROM prior tier + a RANS CFD ground-truth tier, over an (alpha, mach,
// reynolds, flap_deploy) operating grid. The register call seeds both source caches
// and returns a trust map / next-source hint so the composite explorer can read it
// straight away.
//
// Server: thread/thread-api/app/routes/highlift.py
//   POST /projects/{id}/highlift/composite/register?branch=
//
// The trust-map and recommendations reads are the SAME source-neutral composite
// endpoints used elsewhere — we delegate to composite.ts rather than reimplement,
// supplying high-lift-friendly axis defaults (alpha_deg × flap_deploy_deg).

import { apiClient } from "./client";
import {
  fetchRecommendations,
  fetchTrustMap,
  type RecommendationsResponse,
  type TrustMapResponse,
} from "./composite";

// --- register request/response ----------------------------------------------

/** One operating-grid point to seed both the ROM and CFD source caches at. */
export interface RegisterGridPoint {
  alpha_deg: number;
  mach: number;
  reynolds: number;
  flap_deploy_deg?: number | null;
}

/** Plain-flap hinge geometry used to build the prior airfoil + RANS deformation. */
export interface RegisterFlapSpec {
  hinge_x: number;
  hinge_y_frac: number;
  /** Fixed deflection for every point; omit to use each grid point's flap_deploy_deg. */
  deflection_deg?: number | null;
}

export interface RegisterRequest {
  /** Airfoil/design parameters (camber, thickness, …) defining the baseline section. */
  design: Record<string, unknown>;
  flap: RegisterFlapSpec;
  /** Which prior fidelities to bake into the ROM cache. Default ["inviscid"]. */
  fidelities?: string[];
  grid_points?: RegisterGridPoint[];
  rom_source_key?: string;
  cfd_source_key?: string;
  default_reynolds?: number;
}

/** Per-source summary the register call returns for the seeded ROM/CFD caches. */
export interface RegisterSourceSummary {
  source_key?: string;
  artifact_id?: string | null;
  n_rows?: number;
  note?: string | null;
  [key: string]: unknown;
}

export interface RegisterNext {
  sources: string[];
  trust_map: Record<string, unknown>;
}

export interface RegisterResponse {
  project_id: string;
  branch: string;
  grid_points: number;
  rom: RegisterSourceSummary;
  cfd: RegisterSourceSummary;
  cache: Record<string, unknown>;
  next: RegisterNext;
}

/**
 * Register a high-lift composite database (ROM prior + RANS CFD tiers) over the
 * design+operation grid. Seeds both source caches and returns a next-source hint.
 */
export async function registerHighliftComposite(
  projectId: string,
  branch: string,
  body: RegisterRequest,
): Promise<RegisterResponse> {
  const params = new URLSearchParams({ branch });
  return apiClient<RegisterResponse>(
    `/projects/${projectId}/highlift/composite/register?${params.toString()}`,
    { method: "POST", body },
  );
}

// --- thin composite-read wrappers (high-lift axis defaults) ------------------

/**
 * Trust map over the high-lift operating plane. Delegates to composite.ts
 * fetchTrustMap with alpha_deg × flap_deploy_deg axis defaults.
 */
export async function fetchHighliftTrustMap(
  projectId: string,
  branch: string,
  quantity: string,
  axisX: string = "alpha_deg",
  axisY: string = "flap_deploy_deg",
  fixed?: Record<string, number | string>,
): Promise<TrustMapResponse> {
  // The composite engine canonicalises output names to lowercase (e.g. "cl"),
  // so normalise here — callers pass the display name ("CL").
  return fetchTrustMap({ projectId, branch, quantity: quantity.toLowerCase(), axisX, axisY, fixed });
}

/**
 * "What should we run next?" for the high-lift database. Delegates to
 * composite.ts fetchRecommendations with a default budget.
 */
export async function fetchHighliftRecommendations(
  projectId: string,
  branch: string,
  quantity: string,
  budget: number = 8,
): Promise<RecommendationsResponse> {
  return fetchRecommendations({ projectId, branch, quantity: quantity.toLowerCase(), budget });
}

export type { RecommendationsResponse, TrustMapResponse } from "./composite";
